import { Builder } from '@builder.io/react';
import { useEffect, useState } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import Skeleton from 'react-loading-skeleton';
import 'react-lazy-load-image-component/src/effects/blur.css';
import ConvertUSD from '../../../../../../utils/convertUSD';
import CustomButton from '../../../../../../components/general/button';
import ProductBoxTag from '../../../../../../components/general/productBoxTag';
import { useNavigate } from 'react-router-dom';
import { generateLinkWithLang } from '../../../../../../utils/linkGenerator';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import ShowCurrencies from '../../../../../../utils/showCurrencies';
import { useSelector } from 'react-redux';
import { selectSelectedCurrency } from '../../../../../../store/user';
import { selectProducts, selectLastOrders } from '../../../../../../store/products';

const AppealFundraiserProductBox = props => {
  /* ----------------------------- Start variables ---------------------------- */
  const navigate = useNavigate();
  const { defaultProduct, link = '', goal = 1000000, appealsProducts = [], targetDate = '' } = props;
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const [showMore, setShowMore] = useState(false);
  const selectedCurrencyStore = useSelector(selectSelectedCurrency);
  const _allProducts = useSelector(state => selectProducts(state));
  const [mainProduct, setMainProduct] = useState();
  const [totalSale, setTotalSale] = useState(0);
  const [totalSupporters, setTotalSupporters] = useState(0);
  const [showTag, setShowTag] = useState(false);
  const [daysLeft, setDaysLeft] = useState(0);
  const allOrders = useSelector(state => selectLastOrders(state));
  /* -------------------------------------------------------------------------- */

  /* ------------------------------ Start methods ----------------------------- */
  const getMainProduct = _allProducts => {
    // setIsLoaded(true);
    _allProducts.forEach(item => {
      item.sub_categories.forEach(innerItem => {
        innerItem.products.forEach(innerProduct => {
          if (innerProduct.creator === defaultProduct.creator) {
            setMainProduct(innerProduct);
          }
        });
      });
    });
  };
  const handlePercentage = (total_sale, goal) => {
    return `${((Number(total_sale) / Number(goal)) * 100).toFixed(2)}%`;
  };
  /* -------------------------------------------------------------------------- */

  /* ------------------------------- Start hooks ------------------------------ */

  useEffect(() => {
    const now = new Date();
    const target = new Date(targetDate);

    const diffInMs = target - now;
    const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
    const safeDays = diffInDays > 0 ? diffInDays : 1;

    setDaysLeft(safeDays);
    setShowTag(safeDays <= 10);
  }, [targetDate]);
  useEffect(() => {
    // Add "newItem" to commingProducts (temporarily for this calculation)
    let _allOreders;
    if (Array.isArray(allOrders)) {
      _allOreders = allOrders;
    } else if (typeof allOrders === 'object' && allOrders !== null) {
      _allOreders = allOrders.data;
    }
    let normalizedAppealsProducts;
    if (
      Array.isArray(appealsProducts) &&
      appealsProducts.length > 0 &&
      typeof appealsProducts[0] === 'object' &&
      appealsProducts[0] !== null &&
      'creator' in appealsProducts[0]
    ) {
      // Convert array of objects to array of strings
      normalizedAppealsProducts = appealsProducts.map(ap => ap.creator);
    } else {
      // Leave as-is
      normalizedAppealsProducts = appealsProducts;
    }
    if (_allOreders.length) {
      const updatedCommingProducts = [...normalizedAppealsProducts, defaultProduct.creator];
      // Filter orders where creator is in the updated commingProducts
      const filteredOrders = _allOreders.filter(order => updatedCommingProducts.includes(order.creator));
      // Calculate total sale and supporters from filtered orders
      const saleSum = filteredOrders.reduce((sum, order) => sum + order.total_sale, 0);
      const supportersSum = filteredOrders.reduce((sum, order) => sum + order.total_supporters, 0);
      console.log('handling total sale===========',saleSum, supportersSum, filteredOrders, _allOreders, updatedCommingProducts);
      // Update state
      setTotalSale(saleSum);
      setTotalSupporters(supportersSum);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allOrders, appealsProducts]); // Dependencies remain the same

  useEffect(() => {
    selectProducts && _allProducts && Object.keys(_allProducts).length && getMainProduct(_allProducts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_allProducts, selectProducts]);
  /* -------------------------------------------------------------------------- */
  return (
    <>
      {showTag && <ProductBoxTag tags={[`Just ${daysLeft} days left!`]} />}
      <div className="w-full max-[767px]:max-w-[355px] shadow-lg bg-white overflow-hidden mx-auto">
        <div className="overflow-hidden w-full">
          <div
            className="overflow-hidden img-hover-zoom cursor-pointer max-h-[395px] w-full flex items-center"
            onClick={() => navigate(generateLinkWithLang(link || `/appeals/${mainProduct.name}`, lang))}
          >
            {mainProduct ? (
              defaultProduct.img || mainProduct.image_link ? (
                <LazyLoadImage
                  alt={defaultProduct.img?.alt || mainProduct.name}
                  effect="blur"
                  width={`100%`}
                  delayTime={500}
                  onError={({ currentTarget }) => {
                    currentTarget.onerror = null; // prevents looping
                    currentTarget.src = '/images/general/logo-replace.png';
                  }}
                  src={defaultProduct.img?.src || mainProduct.image_link}
                />
              ) : (
                <img className="w-full" src="/images/general/logo-replace.png" alt="" />
              )
            ) : (
              <Skeleton height={344} />
            )}
          </div>
        </div>
        <div className="p-2 md:p-3 md:pb-5">
          <h3
            className="text-[#00a3da] sm:text-md md:text-md lg:text-lg xl:text-xl font-medium h-[48px] lg:h-[56px] mb-2 flex items-center cursor-pointer"
            onClick={() => navigate(generateLinkWithLang(link || `/appeals/${mainProduct.name}`, lang))}
          >
            {mainProduct ? defaultProduct.label || mainProduct.name : <Skeleton height={56} />}
          </h3>
          <div className="relative">
            {mainProduct ? (
              <>
                <div className={`my-2 relative appeals-description-toggle ${showMore ? 'expanded' : 'clamp-4'}`}>
                  <p className={`text-stone-500 text-sm md:text-base`}>{defaultProduct.description || mainProduct.description}</p>
                </div>
                <ChevronDownIcon
                  onClick={() => setShowMore(!showMore)}
                  className={` md:block w-6 h-6 text-[#00a3da] transition-all duration-300 cursor-pointer mx-auto ${
                    showMore ? 'transform rotate-180' : ''
                  }`}
                />
              </>
            ) : (
              <Skeleton count={4} />
            )}
          </div>
          <div className="basis-full flex-col md:basis-1/2 flex justify-center pt-1 md:pt-2">
            <div className="flex items-center justify-between mb-2">
              <div className="flex w-full text-sm">
                <strong className="text-[#00a3da] font-bold mr-0.5">{totalSupporters}</strong>
                <span className="inline-block lg:w-auto mr-1">Supporters,</span>
                <strong className="text-[#00a3da] font-bold mr-0.5">{daysLeft}</strong>
                <span className="inline-block lg:w-auto">days left</span>
              </div>
            </div>
            <div className="flex w-full h-2 bg-[#E3E3E3] mb-2">
              <div
                className={`bg-[#F60362]`}
                style={{
                  width: `${handlePercentage(totalSale, ConvertUSD(selectedCurrencyStore, goal, false))}`,
                }}
              ></div>
            </div>
          </div>
          <div className="basis-full flex justify-between items-center mt-2">
            <div className="flex flex-col w-full">
              <div className="text-lg md:text-2xl text-[#00a3da] basis-full font-bold flex items-center">
                {totalSale && ShowCurrencies(selectedCurrencyStore, Math.round(totalSale), true)}
                {/* <span className="text-xs ml-0.5">{selectedCurrencyStore}</span> */}
              </div>
              <div className="flex flex-wrap w-full text-xs items-center">
                <span className="text-[#78716C] text-xs">Raised of</span>
                <div className="text-[#00a3da] font-bold mx-1 text-sm">
                  {ShowCurrencies(selectedCurrencyStore, Math.round(ConvertUSD(selectedCurrencyStore, goal, false)), false)}
                </div>
                Goal
              </div>
            </div>
            {mainProduct ? (
              <CustomButton
                onClick={() => navigate(generateLinkWithLang(link || `/appeals/${mainProduct.name}`, lang))}
                type="button"
                title={'Donate'}
                className={`!rounded-none`}
              />
            ) : (
              <Skeleton height={43} />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

Builder.registerComponent(AppealFundraiserProductBox, {
  name: 'AppealFundraiserProductBox',
  inputs: [
    {
      name: 'defaultProduct',
      type: 'object',
      defaultValue: {
        img: '',
        creator: '',
        label: '',
        description: '',
      },
      friendlyName: 'Main Product Info',
      subFields: [
        {
          name: 'img',
          type: 'object',
          subFields: [
            { name: 'src', type: 'file', allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'] },
            { name: 'alt', type: 'string' },
          ],
          required: true,
        },
        {
          name: 'creator',
          type: 'string',
          helperText: 'Enter product creator ID',
          required: true,
          hideFromUI: false,
        },
        {
          name: 'label',
          type: 'string',
          helperText: 'Display name for the product',
          required: false,
          hideFromUI: false,
          friendlyName: 'Product Label',
        },
        {
          name: 'description',
          type: 'string',
          helperText: 'Product reference value (usually product ID)',
          required: false,
          hideFromUI: false,
          friendlyName: 'Product Description',
        },
      ],
    },
    {
      name: 'link',
      label: 'Link',
      type: 'string',
      helperText: 'Details page Link (for example: /appeals/palestine-emergency)',
      validation: { required: 'This field is required' },
    },
    {
      name: 'goal',
      label: 'Goal',
      type: 'number',
      defaultValue: 1000000,
    },
    {
      name: 'targetDate',
      type: 'date',
      defaultValue: '',
      friendlyName: 'Target Date',
      required: true,
    },
    {
      name: 'appealsProducts',
      type: 'array',
      subFields: [
        {
          name: 'creator',
          type: 'string',
        },
      ],
    },
  ],
});

export default AppealFundraiserProductBox;
